import React from "react";
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import logo from "../images/Logo-receipt-makerly.png"
import fbThumb from "../images/banner.png";
import loginUI from "../images/receiptmakerly-login.png"
import customReceipts from "../images/online-custom-receipts.png"
import receiptEditor from "../images/custom-receipt-editor.png"

const SEO = ({
  description,
  lang,
  meta,
  image: metaImage,
  title,
  pathname,
  keywords,
  faq,
  sofwareApp,
  mobileApplication,
}) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            keywords
            siteUrl
            image
          }
        }
      }
    `
  );

  const titleConst = title ? title : site.siteMetadata.title;
  const metaDescription = description || site.siteMetadata.description;
  const image =
    metaImage && metaImage.src
      ? `${site.siteMetadata.siteUrl}${metaImage.src}`
      : metaImage
      ? `${site.siteMetadata.siteUrl}${metaImage}`
      : `${site.siteMetadata.siteUrl}${fbThumb}`;

  const canonical = pathname
    ? pathname[pathname.length - 1] === "/"
      ? `${site.siteMetadata.siteUrl}${pathname}`
      : `${site.siteMetadata.siteUrl}${pathname}/`
    : `${site.siteMetadata.siteUrl}`;

  const author = site.siteMetadata.author;

  let faqs = [];
  faq &&
    faq.length > 0 &&
    faq.map((qa) => {
      faqs.push({
        "@type": "Question",
        name: qa.ques,
        acceptedAnswer: {
          "@type": "Answer",
          text: `<p>${qa.ans}</p>`,
        },
      });
    });

  return (
    <>
      <Helmet
        htmlAttributes={{ lang: `en` }}
        // titleTemplate={`${site.siteMetadata.title} | %s`}
      >
        <meta charSet="utf-8" />
        <meta name="HandheldFriendly" content="True" />
        <title>{titleConst}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content={keywords ? keywords : site.siteMetadata.keywords.join(",")}
        />
        <link rel="canonical" href={canonical} />
        <meta name="image" content={image} />
        {/* for Facebook */}
        <meta property="og:site_name" content="Receiptmakerly" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={titleConst} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={image} />
        <meta property="og:image:alt" content={titleConst} />
        <meta property="og:image:width" content="2201" />
        <meta property="og:image:height" content="890" />
        <meta property="og:image:secure_url" content={image} />
        {/* <meta property="fb:app_id" content="2140058186121529" /> */}
        {/* for Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={titleConst} />
        <meta name="twitter:site" content="@receiptmakerly_inc" />
        <meta name="twitter:description" content={metaDescription} />
        <meta property="twitter:image" content={image} />
        <meta name="twitter:creator" content={site.siteMetadata.author} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Receiptmakerly",
            url: "https://receiptmakerly.com/",
            description:
              "An intuitive online receipt maker and receipt generator for businesses. Use customizable templates to generate receipts of various types for your customers",
            logo: "https://receiptmakerly.com/static/Logo-receipt-makerly-4fef41116738fa5a1285a4564966f030.png",
            // foundingDate: "2018",
            // address: {
            //   "@type": "PostalAddress",
            //   addressLocality: "Newark",
            //   addressRegion: "Delaware",
            //   addressCountry: "United States",
            // },
            // contactPoint: {
            //   "@type": "ContactPoint",
            //   contactType: "customer support",
            //   telephone: "[+1 925-452-6102]",
            //   email: "support@apploye.com",
            //   url: "https://support.apploye.com",
            // },
            sameAs: [
              "https://www.facebook.com/receiptmakerly"
            ],
          })}
        </script>

        {faq && faq.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs,
            })}
          </script>
        )}

        {sofwareApp && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              name: "Receiptmakerly",
              operatingSystem:
                "Web platform, Windows, Mac OS X, Linux, iOS, Android",
              applicationCategory: "BusinessApplication",
              description: `An intuitive online receipt maker and receipt generator for businesses. Use customizable templates to generate receipts of various types for your customers`,
              url: "https://receiptmakerly.com/",
              offers: {
                "@type": "Offer",
                price: "4.9",
                priceCurrency: "USD",
              },
              image: [
                site.siteMetadata.siteUrl + loginUI,
                site.siteMetadata.siteUrl + customReceipts,
                site.siteMetadata.siteUrl + receiptEditor,
              ],
              brand: {
                "@type": "Thing",
                name: "Receiptmakerly",
              },
              featureList: sofwareApp.featureList
                ? sofwareApp.featureList
                : `sales receipts, taxi receipts, parking receipts, restaurant receipts, petrol/gas receipts, vehicle toll receipts, grocery receipts, Phone, internet receipts, pharmacy receipts`,
              applicationSubCategory: "Online receipt generator",
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: "5",
                ratingCount: "1",
              },
            })}
          </script>
        )}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://receiptmakerly.com/#website",
            url: "https://receiptmakerly.com/",
            name: "Receiptmakerly",
            description:
              "An intuitive online receipt maker and receipt generator for businesses. Use customizable templates to generate receipts of various types for your customers",
            potentialAction: [
              {
                "@type": "SearchAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate:
                    "https://receiptmakerly.com/?s={search_term_string}",
                },
                "query-input": "required name=search_term_string",
              },
            ],
            inLanguage: "en-US",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": canonical,
            url: canonical,
            name: titleConst,
            isPartOf: {
              "@id": "https://receiptmakerly.com/#website",
            },
            // "datePublished": "2020-12-06T16:31:52+00:00",
            // "dateModified": "2022-11-03T08:12:12+00:00",
            description: description,
            breadcrumb: {
              "@id": `${canonical}#breadcrumb`,
            },
            inLanguage: "en-US",
            potentialAction: [
              {
                "@type": "ReadAction",
                target: [canonical],
              },
            ],
          })}
        </script>
      </Helmet>
    </>
  );
};

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  pathname: PropTypes.string,
};

export default SEO;
